import { assertNever } from "@redotech/util/type";
import * as z from "zod/v4";

export const shopifyDisputeStatus = [
  "ACCEPTED",
  "CHARGE_REFUNDED",
  "LOST",
  "NEEDS_RESPONSE",
  "UNDER_REVIEW",
  "WON",
] as const;

export type ShopifyDisputeStatus = (typeof shopifyDisputeStatus)[number];

export const shopifyDisputeType = ["CHARGEBACK", "INQUIRY"] as const;

export type ShopifyDisputeType = (typeof shopifyDisputeType)[number];

export const ShopifyDisputeItemSchema_ = z.object({
  id: z.string(),
  legacyResourceId: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.enum(shopifyDisputeStatus),
  type: z.enum(shopifyDisputeType),
  reason: z.string(),
  initiatedAt: z.string(),
  evidenceDueBy: z.nullable(z.string()),
  evidenceSentOn: z.nullable(z.string()),
  finalizedOn: z.nullable(z.string()),
  orderName: z.nullable(z.string()),
});

export const ShopifyDisputeItemSchema: z.ZodType<ShopifyDisputeItem> =
  ShopifyDisputeItemSchema_;
export interface ShopifyDisputeItem
  extends z.infer<typeof ShopifyDisputeItemSchema_> {}

// Evidence file upload

export const shopifyDisputeEvidenceFileType = [
  "CANCELLATION_POLICY_FILE",
  "CUSTOMER_COMMUNICATION_FILE",
  "REFUND_POLICY_FILE",
  "SERVICE_DOCUMENTATION_FILE",
  "SHIPPING_DOCUMENTATION_FILE",
  "UNCATEGORIZED_FILE",
] as const;

export type ShopifyDisputeEvidenceFileType =
  (typeof shopifyDisputeEvidenceFileType)[number];

const ShopifyDisputeFileUploadSchema_ = z.object({
  id: z.string(),
  originalFileName: z.nullable(z.string()),
  fileSize: z.number(),
  fileType: z.string(),
  url: z.string(),
  disputeEvidenceType: z.nullable(z.enum(shopifyDisputeEvidenceFileType)),
});

export const ShopifyDisputeFileUploadSchema: z.ZodType<ShopifyDisputeFileUpload> =
  ShopifyDisputeFileUploadSchema_;
export interface ShopifyDisputeFileUpload
  extends z.infer<typeof ShopifyDisputeFileUploadSchema_> {}

// Fulfillment info

const ShopifyDisputeFulfillmentSchema_ = z.object({
  id: z.string(),
  shippingCarrier: z.nullable(z.string()),
  shippingTrackingNumber: z.nullable(z.string()),
  shippingDate: z.nullable(z.string()),
});

export const ShopifyDisputeFulfillmentSchema: z.ZodType<ShopifyDisputeFulfillment> =
  ShopifyDisputeFulfillmentSchema_;
export interface ShopifyDisputeFulfillment
  extends z.infer<typeof ShopifyDisputeFulfillmentSchema_> {}

// Evidence

const ShopifyDisputeEvidenceSchema_ = z.object({
  id: z.string(),
  submitted: z.boolean(),
  accessActivityLog: z.nullable(z.string()),
  cancellationPolicyDisclosure: z.nullable(z.string()),
  cancellationRebuttal: z.nullable(z.string()),
  refundPolicyDisclosure: z.nullable(z.string()),
  refundRefusalExplanation: z.nullable(z.string()),
  uncategorizedText: z.nullable(z.string()),
  productDescription: z.nullable(z.string()),
  customerEmailAddress: z.nullable(z.string()),
  customerFirstName: z.nullable(z.string()),
  customerLastName: z.nullable(z.string()),
  customerPurchaseIp: z.nullable(z.string()),
  cancellationPolicyFile: z.nullable(ShopifyDisputeFileUploadSchema),
  customerCommunicationFile: z.nullable(ShopifyDisputeFileUploadSchema),
  refundPolicyFile: z.nullable(ShopifyDisputeFileUploadSchema),
  serviceDocumentationFile: z.nullable(ShopifyDisputeFileUploadSchema),
  shippingDocumentationFile: z.nullable(ShopifyDisputeFileUploadSchema),
  uncategorizedFile: z.nullable(ShopifyDisputeFileUploadSchema),
  fulfillments: z.array(ShopifyDisputeFulfillmentSchema),
});

export const ShopifyDisputeEvidenceSchema: z.ZodType<ShopifyDisputeEvidence> =
  ShopifyDisputeEvidenceSchema_;
export interface ShopifyDisputeEvidence
  extends z.infer<typeof ShopifyDisputeEvidenceSchema_> {}

// Detail (extends list item with evidence)

const ShopifyDisputeDetailSchema_ = ShopifyDisputeItemSchema_.extend({
  networkReasonCode: z.nullable(z.string()),
  evidence: z.nullable(ShopifyDisputeEvidenceSchema),
});

export const ShopifyDisputeDetailSchema: z.ZodType<ShopifyDisputeDetail> =
  ShopifyDisputeDetailSchema_;
export interface ShopifyDisputeDetail
  extends z.infer<typeof ShopifyDisputeDetailSchema_> {}

// Evidence categories for the UI

export const shopifyDisputeEvidenceCategory = [
  "CUSTOMER_COMMUNICATION",
  "SHIPPING_DOCUMENTATION",
  "UNCATEGORIZED",
] as const;

export type ShopifyDisputeEvidenceCategory =
  (typeof shopifyDisputeEvidenceCategory)[number];

// Timeline steps

export const disputeTimelineStep = [
  "DISPUTE_CREATED",
  "SUBMIT_EVIDENCE",
  "BANK_REVIEW",
  "DISPUTE_SETTLED",
] as const;

export type DisputeTimelineStep = (typeof disputeTimelineStep)[number];

export function getActiveTimelineStep(
  status: ShopifyDisputeStatus,
): DisputeTimelineStep {
  switch (status) {
    case "NEEDS_RESPONSE":
      return "SUBMIT_EVIDENCE";
    case "UNDER_REVIEW":
      return "BANK_REVIEW";
    case "WON":
    case "LOST":
    case "ACCEPTED":
    case "CHARGE_REFUNDED":
      return "DISPUTE_SETTLED";
    default:
      assertNever(status);
  }
}
